import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, FolderOpen, Plus, BookOpen, BarChart3, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export default function ModulesList({ user }) {
  const [modules, setModules] = useState([]);
  const [moduleCourses, setModuleCourses] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      const [modulesRes, coursesRes] = await Promise.all([
        fetch(`${API}/modules`, { headers: getAuthHeaders() }),
        fetch(`${API}/courses`, { headers: getAuthHeaders() })
      ]);

      if (modulesRes.ok && coursesRes.ok) {
        const modulesData = await modulesRes.json();
        const coursesData = await coursesRes.json();

        setModules(modulesData);

        // Count courses per module
        const counts = {};
        coursesData.forEach(course => {
          if (course.module_id) {
            counts[course.module_id] = (counts[course.module_id] || 0) + 1;
          }
        });
        setModuleCourses(counts);
      }
    } catch (error) {
      toast.error('Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteModule = async (moduleId, moduleName) => {
    const courseCount = moduleCourses[moduleId] || 0;
    
    if (courseCount > 0) {
      toast.error(`Cannot delete "${moduleName}": ${courseCount} course(s) are associated with it`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${moduleName}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(moduleId);
    try {
      const response = await fetch(`${API}/modules/${moduleId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Module deleted successfully');
        setModules(modules.filter(m => m.module_id !== moduleId));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete module');
      }
    } catch (error) {
      toast.error('Failed to delete module');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <div className="text-[#3A5A40] text-lg">Loading modules...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Back to Dashboard
          </Button>
          {user?.role === 'teacher' && (
            <Button
              onClick={() => navigate('/modules/create')}
              data-testid="create-module-button"
              className="bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 rounded-lg"
            >
              <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Create Module
            </Button>
          )}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-6 md:p-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <FolderOpen className="w-10 h-10 text-[#3A5A40]" strokeWidth={1.5} />
            <h1 className="text-4xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
              Course Modules
            </h1>
          </div>
          <p className="text-lg text-[#588157]">Organized learning paths for your spiritual journey</p>
        </div>

        {modules.length === 0 ? (
          <Card className="p-12 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
            <FolderOpen className="w-20 h-20 text-[#A3B18A] mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-xl font-bold text-[#344E41] mb-2">No Modules Yet</h3>
            <p className="text-[#588157] mb-6">
              {user?.role === 'teacher' 
                ? 'Create your first module to organize courses into learning paths'
                : 'Check back soon for organized learning modules'}
            </p>
            {user?.role === 'teacher' && (
              <Button
                onClick={() => navigate('/modules/create')}
                className="bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 rounded-lg"
              >
                <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Create First Module
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module) => {
              const courseCount = moduleCourses[module.module_id] || 0;
              const canDelete = courseCount === 0;
              
              return (
                <Card
                  key={module.module_id}
                  className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm hover:shadow-md duration-300 hover:-translate-y-1 rounded-2xl"
                  data-testid={`module-card-${module.module_id}`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-3 bg-[#3A5A40]/10 rounded-xl flex-shrink-0">
                      <FolderOpen className="w-8 h-8 text-[#3A5A40]" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                        {module.name}
                      </h3>
                      <p className="text-sm text-[#588157] line-clamp-3">
                        {module.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mb-4 py-3 border-t border-[#CCD5AE]">
                    <div className="flex items-center gap-2 text-sm text-[#588157]">
                      <BookOpen className="w-4 h-4" strokeWidth={1.5} />
                      <span>{courseCount} course{courseCount !== 1 ? 's' : ''}</span>
                    </div>
                    {user?.role === 'teacher' && canDelete && (
                      <span className="text-xs text-[#A3B18A] ml-auto">Can be deleted</span>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate(`/courses?module=${module.module_id}`)}
                      variant="outline"
                      className="flex-1 border-2 border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white duration-300 rounded-lg text-sm"
                      data-testid={`view-courses-${module.module_id}`}
                    >
                      View Courses
                    </Button>
                    {user?.role === 'teacher' && (
                      <>
                        <Button
                          onClick={() => navigate(`/modules/${module.module_id}/report`)}
                          className="flex-1 bg-[#D4A373] text-white hover:bg-[#C89563] duration-200 rounded-lg text-sm"
                          data-testid={`view-report-${module.module_id}`}
                        >
                          <BarChart3 className="w-4 h-4 mr-1" strokeWidth={1.5} />
                          Report
                        </Button>
                        <Button
                          onClick={() => handleDeleteModule(module.module_id, module.name)}
                          disabled={!canDelete || deleting === module.module_id}
                          variant="outline"
                          className={`px-3 border-2 rounded-lg text-sm ${
                            canDelete 
                              ? 'border-red-400 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500' 
                              : 'border-[#CCD5AE] text-[#A3B18A] cursor-not-allowed'
                          }`}
                          data-testid={`delete-module-${module.module_id}`}
                          title={canDelete ? 'Delete module' : 'Cannot delete: has courses'}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
